import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { AgentConversationSummary, ConversationStatus } from '../api/agentRuntimeSchemas';
import { getConversation, listConversations, releaseConversation, takeOverConversation } from '../api/endpoints/agentRuntime';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { errorText, isRuntimeOff, LIVE_POLL_MS } from '../features/agents/reception/useReception';

const LIST_KEY = ['agents-conversations'] as const;
const WHEN = new Intl.DateTimeFormat('es-PA', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
const STATUS_LABEL: Readonly<Record<ConversationStatus, string>> = { bot: 'Luna', human: 'Tu equipo', closed: 'Cerrada' };
const STATUS_TONE: Readonly<Record<ConversationStatus, string>> = { bot: 'good', human: 'due', closed: 'off' };
const FILTERS: readonly { value: ConversationStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'human', label: 'Esperan a tu equipo' },
  { value: 'bot', label: 'Con Luna' },
];

function who(conversation: AgentConversationSummary): string {
  return conversation.customerName ?? conversation.customerPhone;
}

function Thread({ id }: { id: string }) {
  const client = useQueryClient();
  const threadQuery = useQuery({ queryKey: ['agents-conversation', id], queryFn: () => getConversation(id), refetchInterval: LIVE_POLL_MS });
  const toggle = useMutation({
    mutationFn: (take: boolean) => (take ? takeOverConversation(id) : releaseConversation(id)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['agents-conversation', id] });
      void client.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
  if (threadQuery.isLoading) return <LoadingState label="Abriendo la conversación…" />;
  if (threadQuery.error) return <ErrorBanner message={errorText(threadQuery.error, 'No pudimos abrir la conversación.')} />;
  const thread = threadQuery.data;
  if (!thread) return null;
  const human = thread.conversation.status === 'human';
  return (
    <section className="inbox-thread" aria-label={`Conversación con ${thread.conversation.customerName ?? thread.conversation.customerPhone}`}>
      <header className="inbox-thread-head">
        <div>
          <p className="row-title">{thread.conversation.customerName ?? thread.conversation.customerPhone}</p>
          <p className="row-sub">
            {thread.conversation.customerPhone}
            {thread.conversation.handoffReason ? ` · Luna la pasó: ${thread.conversation.handoffReason}` : ''}
          </p>
        </div>
        <button type="button" className={human ? 'btn small secondary' : 'btn small'} disabled={toggle.isPending} onClick={() => toggle.mutate(!human)}>
          {human ? 'Devolver a Luna' : 'Tomar la conversación'}
        </button>
      </header>
      {toggle.error ? <ErrorBanner message={errorText(toggle.error, 'No pudimos cambiarlo. Intenta de nuevo.')} /> : null}
      <ol className="inbox-messages">
        {thread.messages.map((message) => (
          <li key={message.id} className="inbox-message" data-direction={message.direction} data-source={message.source}>
            <p className="team-bubble">{message.text}</p>
            <p className="inbox-when">
              {message.source === 'agent' ? 'Luna' : message.source === 'staff' ? 'Tu equipo' : 'Cliente'} · {WHEN.format(new Date(message.at))}
            </p>
          </li>
        ))}
      </ol>
      {human ? <p className="notice">Contesta desde el WhatsApp del negocio. Luna no escribe mientras la conversación es tuya.</p> : null}
    </section>
  );
}

/**
 * Bandeja (Dale Agents): what Luna talked about with customers on the
 * business's WhatsApp. Any conversation can be taken by the team.
 */
export function BandejaPage() {
  const [filter, setFilter] = useState<ConversationStatus | 'all'>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const listQuery = useQuery({
    queryKey: [...LIST_KEY, filter],
    queryFn: () => listConversations(filter === 'all' ? {} : { status: filter }),
    refetchInterval: LIVE_POLL_MS * 5,
    retry: false,
  });
  const conversations = listQuery.data?.data ?? [];
  const selected = openId ?? conversations[0]?.id ?? null;

  return (
    <main className="inbox-page">
      <PageHeader title="Bandeja" description="Lo que Luna conversa con tus clientes en WhatsApp." />
      <div className="seg" role="group" aria-label="Filtrar conversaciones">
        {FILTERS.map((option) => (
          <button key={option.value} type="button" aria-pressed={filter === option.value} onClick={() => setFilter(option.value)}>
            {option.label}
          </button>
        ))}
      </div>
      {listQuery.isLoading ? <LoadingState label="Trayendo conversaciones…" /> : null}
      {isRuntimeOff(listQuery.error) ? <p className="notice">Luna todavía no está conectada. Enciéndela en Dale Agents.</p> : null}
      {listQuery.error && !isRuntimeOff(listQuery.error) ? <ErrorBanner message={errorText(listQuery.error, 'No pudimos traer las conversaciones.')} /> : null}
      {listQuery.data && conversations.length === 0 ? (
        <EmptyState title="Todavía no hay conversaciones" description="Cuando un cliente le escriba al WhatsApp del negocio, lo ves aquí." />
      ) : null}
      {conversations.length > 0 ? (
        <div className="inbox-layout">
          <ul className="inbox-list group rows">
            {conversations.map((conversation) => (
              <li key={conversation.id}>
                <button type="button" className="row inbox-item" aria-current={conversation.id === selected || undefined} onClick={() => setOpenId(conversation.id)}>
                  <div className="grow">
                    <p className="row-title">{who(conversation)}</p>
                    <p className="row-sub inbox-preview">{conversation.lastMessage?.text ?? 'Sin mensajes'}</p>
                  </div>
                  <div className="inbox-meta">
                    <span className="status" data-tone={STATUS_TONE[conversation.status]}>
                      {STATUS_LABEL[conversation.status]}
                    </span>
                    <span className="inbox-when">{WHEN.format(new Date(conversation.lastMessageAt))}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          {selected ? <Thread key={selected} id={selected} /> : null}
        </div>
      ) : null}
    </main>
  );
}
